import { __DEV__ } from '../../lib/__dev__';

import { useSelectedHomeyConnection } from '../../store/SelectedHomeyStore.hooks';

import styles from './ConnectionStatusPill.module.css';

export function ConnectionStatusPill() {
  const connection = useSelectedHomeyConnection();

  if (__DEV__ === false) {
    return null;
  }

  const pill = getConnectionPill(connection);

  if (pill == null) {
    return null;
  }

  return (
    <div className={styles.root}>
      <div
        aria-label={pill.ariaLabel}
        className={styles.inner}
        data-state={pill.state}
        title={pill.title}
      >
        <div className={styles.status}>
          <div className={styles.dot} data-state={pill.state} />
          <div className={styles.label}>{pill.statusLabel}</div>
        </div>

        <div className={styles.divider} />

        <div className={styles.meta}>{pill.routeLabel}</div>

        {pill.detailLabel != null && (
          <>
            <div className={styles.divider} />
            <div className={styles.meta}>{pill.detailLabel}</div>
          </>
        )}
      </div>
    </div>
  );
}

function getConnectionPill(connection) {
  if (
    connection.connectionStatus !== 'disconnected' &&
    connection.connectionStatus !== 'recovered'
  ) {
    return null;
  }

  const statusLabel = connection.connectionStatus === 'recovered' ? 'Recovered' : 'Disconnected';
  const routeLabel = `Route ${getRouteLabel(connection.transportType)}`;
  const detailLabel = getDetailLabel(connection);
  const lines = [
    `Status: ${statusLabel}`,
    `Route: ${getRouteLabel(connection.transportType)}`,
    `Attempts: ${connection.reconnectAttempt}`,
    connection.strategyId != null ? `Strategy: ${connection.strategyId}` : null,
    connection.lastConnectedAt != null
      ? `Connected since: ${formatConnectionTime(connection.lastConnectedAt)}`
      : null,
    connection.disconnectStartedAt != null
      ? `Disconnected at: ${formatConnectionTime(connection.disconnectStartedAt)}`
      : null,
    connection.lastRecoveredAt != null && connection.connectionStatus === 'recovered'
      ? `Recovered at: ${formatConnectionTime(connection.lastRecoveredAt)}`
      : null,
    connection.lastDisconnectDurationMs > 0
      ? `Disconnect duration: ${formatDuration(connection.lastDisconnectDurationMs)}`
      : null,
    connection.baseUrl != null ? `Base URL: ${connection.baseUrl}` : null,
  ].filter(Boolean);

  return {
    ariaLabel: lines.join('. '),
    detailLabel,
    routeLabel,
    state: connection.connectionStatus,
    statusLabel,
    title: lines.join('\n'),
  };
}

function getDetailLabel(connection) {
  if (connection.connectionStatus === 'recovered' && connection.lastDisconnectDurationMs > 0) {
    return `Down ${formatDuration(connection.lastDisconnectDurationMs)}`;
  }

  if (connection.reconnectAttempt > 0) {
    return `Attempt ${connection.reconnectAttempt}`;
  }

  return null;
}

function getRouteLabel(transportType) {
  if (transportType === 'local') {
    return 'Local';
  }

  if (transportType === 'direct') {
    return 'Direct';
  }

  if (transportType === 'relayed') {
    return 'Relayed';
  }

  return 'Unknown';
}

function formatConnectionTime(value) {
  return new Date(value).toLocaleTimeString();
}

function formatDuration(value) {
  const totalSeconds = Math.max(1, Math.round(value / 1000));
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;

  if (minutes === 0) {
    return `${totalSeconds}s`;
  }

  if (seconds === 0) {
    return `${minutes}m`;
  }

  return `${minutes}m ${seconds}s`;
}
